import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  AdminContact,
  AdminContactDetail,
  AdminContactMerge,
  AdminContactSourceBadge,
} from "@tih/shared";
import {
  ApiError,
  adminBulkAddContacts,
  adminCreateBookContact,
  adminDeleteBookContact,
  adminDeleteBookContacts,
  adminFetchContactDetail,
  adminFetchContactMerges,
  adminFetchContactsBook,
  adminMergeContacts,
  adminUndoContactMerge,
  adminUpdateBookContact,
} from "../../api/client";

const EMPTY_FORM = { name: "", phone: "", email: "", tags: "", notes: "" };

const BADGE_LABEL: Record<AdminContactSourceBadge, string> = {
  address_book: "Address book",
  attendee: "Attendee",
  subscriber: "Subscriber",
  member: "Member",
};

function parseTagInput(value: string): string[] {
  return value
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

type Props = {
  /** When true, show checkboxes and report selection (SMS compose). */
  selectable?: boolean;
  selectedIds?: number[];
  onSelectedChange?: (ids: number[]) => void;
  /** Compact heading when embedded in Communication. */
  embedded?: boolean;
};

export function AddressBookPanel({
  selectable = false,
  selectedIds,
  onSelectedChange,
  embedded = false,
}: Props) {
  const formRef = useRef<HTMLElement>(null);
  const [contacts, setContacts] = useState<AdminContact[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [needsPhoneFix, setNeedsPhoneFix] = useState(false);
  const [internalSelected, setInternalSelected] = useState<Set<number>>(new Set());
  const selected = useMemo(() => {
    if (selectedIds) return new Set(selectedIds);
    return internalSelected;
  }, [selectedIds, internalSelected]);

  function setSelected(next: Set<number>) {
    if (onSelectedChange) onSelectedChange([...next]);
    else setInternalSelected(next);
  }

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkTags, setBulkTags] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminContactDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [merges, setMerges] = useState<AdminContactMerge[]>([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSurvivorId, setMergeSurvivorId] = useState<number | null>(null);

  async function loadContacts() {
    const [payload, mergeRows] = await Promise.all([
      adminFetchContactsBook(),
      adminFetchContactMerges().catch(() => [] as AdminContactMerge[]),
    ]);
    setContacts(payload.contacts);
    setAllTags(payload.tags);
    setMerges(mergeRows);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadContacts();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Could not load address book");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const invalidPhoneCount = useMemo(
    () => contacts.filter((c) => Boolean(c.phone) && !c.phoneValid).length,
    [contacts],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (needsPhoneFix && !(c.phone && !c.phoneValid)) return false;
      if (tagFilter && !c.tags.includes(tagFilter)) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.phone ?? "").toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q) ||
        c.tags.some((t) => t.includes(q)) ||
        c.badges.some((b) => BADGE_LABEL[b].toLowerCase().includes(q))
      );
    });
  }, [contacts, search, tagFilter, needsPhoneFix]);

  const selectedList = [...selected];

  function toggleSelected(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAllVisible() {
    const next = new Set(selected);
    const allOn = visible.length > 0 && visible.every((c) => next.has(c.id));
    if (allOn) visible.forEach((c) => next.delete(c.id));
    else visible.forEach((c) => next.add(c.id));
    setSelected(next);
  }

  function startAdd() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  function startEdit(row: AdminContact) {
    setEditingId(row.id);
    setForm({
      name: row.name,
      phone: row.phone ?? "",
      email: row.email ?? "",
      tags: row.tags.join(", "),
      notes: row.notes ?? "",
    });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  }

  async function saveContact(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        tags: parseTagInput(form.tags),
        notes: form.notes.trim(),
      };
      if (editingId != null) {
        await adminUpdateBookContact(editingId, payload);
        setSuccess("Contact updated.");
      } else {
        await adminCreateBookContact(payload);
        setSuccess("Contact added.");
      }
      closeForm();
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save contact");
    } finally {
      setBusy(false);
    }
  }

  async function toggleSubscribed(row: AdminContact) {
    setBusy(true);
    setError(null);
    try {
      await adminUpdateBookContact(row.id, { subscribed: !row.subscribed });
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update contact");
    } finally {
      setBusy(false);
    }
  }

  async function removeContact(row: AdminContact) {
    if (!confirm(`Remove ${row.name} from the address book?`)) return;
    setBusy(true);
    setError(null);
    try {
      await adminDeleteBookContact(row.id);
      setSuccess("Contact removed.");
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove contact");
    } finally {
      setBusy(false);
    }
  }

  async function removeSelected() {
    if (selectedList.length === 0) return;
    if (!confirm(`Remove ${selectedList.length} contacts from the address book?`)) return;
    setBusy(true);
    setError(null);
    try {
      const result = await adminDeleteBookContacts(selectedList);
      setSelected(new Set());
      setSuccess(`Removed ${result.deleted} contacts.`);
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove contacts");
    } finally {
      setBusy(false);
    }
  }

  async function runBulkAdd() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminBulkAddContacts({
        text: bulkText,
        tags: parseTagInput(bulkTags),
      });
      setSuccess(
        `Paste import: ${result.created} added, ${result.updated} updated` +
          (result.skipped ? `, ${result.skipped} skipped` : "") +
          ".",
      );
      setBulkText("");
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not import");
    } finally {
      setBusy(false);
    }
  }


  function openMergeDialog() {
    if (selectedList.length < 2) return;
    const picked =
      contacts.find((c) => selected.has(c.id) && c.phone) ??
      contacts.find((c) => selected.has(c.id) && c.userId != null) ??
      contacts.find((c) => selected.has(c.id)) ??
      null;
    setMergeSurvivorId(picked?.id ?? selectedList[0] ?? null);
    setMergeOpen(true);
  }

  async function confirmMerge() {
    if (mergeSurvivorId == null || selectedList.length < 2) return;
    const mergeIds = selectedList.filter((id) => id !== mergeSurvivorId);
    const survivor = contacts.find((c) => c.id === mergeSurvivorId);
    if (
      !confirm(
        `Merge ${mergeIds.length} contact(s) into “${survivor?.name ?? mergeSurvivorId}”?\n\n` +
          "This soft-merges duplicates (undoable from Merge history). Past attendance stays on EventAttendee rows.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminMergeContacts({
        survivorId: mergeSurvivorId,
        mergeIds,
      });
      setSuccess(
        `Merged ${result.mergedIds.length} contact(s) into #${result.survivorId}.`,
      );
      setMergeOpen(false);
      setSelected(new Set());
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not merge contacts");
    } finally {
      setBusy(false);
    }
  }

  async function undoMerge(mergeId: number) {
    if (!confirm("Undo this merge and restore the absorbed contact?")) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await adminUndoContactMerge(mergeId);
      setSuccess("Merge undone — contact restored.");
      await loadContacts();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not undo merge");
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    const header = ["name", "phone", "email", "tags", "subscribed", "source", "badges"];
    const lines = [
      header.join(","),
      ...visible.map((c) =>
        [
          c.name,
          c.phone ?? "",
          c.email ?? "",
          c.tags.join(";"),
          c.subscribed ? "yes" : "no",
          c.source,
          c.badges.map((b) => BADGE_LABEL[b]).join(";"),
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "address-book.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function toggleExpand(row: AdminContact) {
    if (expandedId === row.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(row.id);
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await adminFetchContactDetail(row.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load person detail");
    } finally {
      setDetailLoading(false);
    }
  }

  if (loading) {
    return <div style={{ opacity: 0.6 }}>Loading address book…</div>;
  }

  return (
    <>
      {!embedded && (
        <header className="admin-page-header" style={{ marginBottom: 16 }}>
          <h1>Address book</h1>
          <p>Manage everyone — add, paste import, merge, attendance history, and identity links.</p>
        </header>
      )}

      {error && (
        <div className="admin-banner admin-banner-error" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="admin-banner admin-banner-success" role="status">
          {success}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          type="search"
          placeholder="Search name, mobile, email, tag…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 220 }}
        />
        <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
          <option value="">All tags</option>
          {allTags.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`btn ${needsPhoneFix ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setNeedsPhoneFix((v) => !v)}
          title="Show contacts whose stored mobile fails SMS validation"
        >
          Needs phone fix{invalidPhoneCount ? ` (${invalidPhoneCount})` : ""}
        </button>
        <button type="button" className="btn btn-primary" onClick={startAdd}>
          + Add contact
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setBulkOpen((v) => !v)}
        >
          {bulkOpen ? "Close import" : "Paste import"}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={exportCsv}
          disabled={visible.length === 0}
        >
          Export CSV
        </button>
        {selectedList.length >= 2 && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openMergeDialog()}
            disabled={busy}
          >
            Merge selected ({selectedList.length})…
          </button>
        )}
        {(selectable || selectedList.length > 0) && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void removeSelected()}
            disabled={busy || selectedList.length === 0}
          >
            Remove selected ({selectedList.length})
          </button>
        )}
      </div>

      {mergeOpen && (
        <section className="admin-card" style={{ marginBottom: 24 }}>
          <h2 className="admin-card-title">Merge contacts</h2>
          <p className="admin-card-sub">
            Pick the survivor row. Others are soft-merged (hidden, undoable). Different
            phones are kept as “Also: …” notes so uniqueness is preserved.
          </p>
          <div className="admin-blog-form-fields">
            {selectedList.map((id) => {
              const row = contacts.find((c) => c.id === id);
              if (!row) return null;
              return (
                <label key={id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="radio"
                    name="merge-survivor"
                    checked={mergeSurvivorId === id}
                    onChange={() => setMergeSurvivorId(id)}
                  />
                  <span>
                    <strong>{row.name}</strong>
                    {" · "}
                    {row.phone ?? "no phone"}
                    {" · "}
                    {row.email ?? "no email"}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="admin-blog-form-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || mergeSurvivorId == null}
              onClick={() => void confirmMerge()}
            >
              Confirm merge
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => setMergeOpen(false)}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {bulkOpen && (
        <section className="admin-card" style={{ marginBottom: 24 }}>
          <h2 className="admin-card-title">Paste import</h2>
          <div className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Paste a list — one per line, e.g. Grace, 0700123456</span>
                <textarea
                  rows={6}
                  value={bulkText}
                  disabled={busy}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"Grace M, 0700123456\nDaniel 0772000111\n+256700987654"}
                />
              </label>
              <label>
                <span>Tags for these contacts (comma separated, optional)</span>
                <input
                  value={bulkTags}
                  disabled={busy}
                  onChange={(e) => setBulkTags(e.target.value)}
                  placeholder="choir, volunteers"
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !bulkText.trim()}
                onClick={() => void runBulkAdd()}
              >
                Add pasted contacts
              </button>
            </div>
          </div>
          <p className="admin-card-foot">
            Lines without a usable SMS mobile (too short, not a Uganda 07… number, or
            obvious junk) are skipped. Sync subscribers / members / attendees and pull
            from RSVPs live under Settings → Address book.
          </p>
        </section>
      )}

      {visible.length === 0 ? (
        <div style={{ opacity: 0.6 }}>
          {contacts.length === 0
            ? "No contacts yet — add one or paste a list. For sync / backfill, use Settings → Address book."
            : needsPhoneFix
              ? "No contacts need a phone fix."
              : "No contacts match that search."}
        </div>
      ) : (
        <div className="admin-table-wrap" style={{ marginBottom: 24 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    checked={
                      visible.length > 0 && visible.every((c) => selected.has(c.id))
                    }
                    onChange={toggleAllVisible}
                  />
                </th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Source</th>
                <th>Tags</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <Fragment key={row.id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.name}`}
                        checked={selected.has(row.id)}
                        onChange={() => toggleSelected(row.id)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: 0, textAlign: "left" }}
                        onClick={() => void toggleExpand(row)}
                      >
                        {row.name}
                        {expandedId === row.id ? " ▾" : " ▸"}
                      </button>
                      {row.email && (
                        <div className="admin-muted" style={{ fontSize: "0.85em" }}>
                          {row.email}
                        </div>
                      )}
                      {row.userName && (
                        <div className="admin-muted" style={{ fontSize: "0.85em" }}>
                          Linked: {row.userName}
                        </div>
                      )}
                    </td>
                    <td>
                      {row.phone ?? "—"}
                      {row.phone && !row.phoneValid ? (
                        <div>
                          <span className="admin-badge admin-badge-muted" title="Won't receive SMS until fixed">
                            Invalid phone
                          </span>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {row.badges.map((b) => (
                          <span key={b} className="admin-badge">
                            {BADGE_LABEL[b]}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td>{row.tags.length ? row.tags.join(", ") : "—"}</td>
                    <td>
                      <span
                        className={`admin-badge ${
                          row.subscribed ? "admin-badge-upcoming" : ""
                        }`}
                      >
                        {row.subscribed ? "SMS on" : "Opted out"}
                      </span>
                    </td>
                    <td className="admin-row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={busy}
                        onClick={() => void toggleSubscribed(row)}
                      >
                        {row.subscribed ? "Opt out" : "Resubscribe"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={busy}
                        onClick={() => startEdit(row)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={() => void removeContact(row)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr>
                      <td colSpan={7}>
                        {detailLoading || !detail ? (
                          <div style={{ opacity: 0.6, padding: 12 }}>Loading…</div>
                        ) : (
                          <div style={{ padding: 12 }}>
                            <strong>Attendance history</strong>
                            {detail.attendance.length === 0 ? (
                              <p className="admin-muted">No matching EventAttendee rows yet.</p>
                            ) : (
                              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                                {detail.attendance.map((a) => (
                                  <li key={a.id}>
                                    {a.eventTitle?.trim() || "Overnight"} ·{" "}
                                    {new Date(a.eventStartsAt).toLocaleString()}
                                    {a.checkedInAt
                                      ? ` · checked in ${new Date(a.checkedInAt).toLocaleString()}`
                                      : " · on list"}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <p className="admin-muted" style={{ marginTop: 8 }}>
                              Subscriber list: {detail.isSubscriber ? "yes" : "no"}
                              {detail.userId != null
                                ? ` · Member user #${detail.userId}${
                                    detail.userName ? ` (${detail.userName})` : ""
                                  }`
                                : " · No linked user"}
                            </p>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <section ref={formRef} className="admin-card">
          <h2 className="admin-card-title">
            {editingId != null ? "Edit contact" : "Add contact"}
          </h2>
          <form onSubmit={(e) => void saveContact(e)} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Name</span>
                <input
                  value={form.name}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Grace M."
                  required
                />
              </label>
              <label>
                <span>Mobile number</span>
                <input
                  value={form.phone}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="0700123456"
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </label>
              <label>
                <span>Tags (comma separated)</span>
                <input
                  value={form.tags}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                  placeholder="choir, volunteers"
                />
              </label>
              <label>
                <span>Notes (optional)</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button type="button" className="btn btn-ghost" onClick={closeForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : "Save contact"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="admin-card" style={{ marginTop: 24 }}>
        <h2 className="admin-card-title">Merge history</h2>
        <p className="admin-card-sub">
          Soft-merges keep attendance history intact. Undo restores the absorbed contact
          when the phone / member link is still free.
        </p>
        {merges.length === 0 ? (
          <p className="admin-muted">No merges yet.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Survivor</th>
                  <th>Merged</th>
                  <th>By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {merges.map((m) => (
                  <tr key={m.id}>
                    <td>{new Date(m.createdAt).toLocaleString()}</td>
                    <td>
                      #{m.survivorId} {m.survivorName}
                    </td>
                    <td>
                      #{m.mergedId} {m.mergedName}
                      {m.undoneAt ? (
                        <div className="admin-muted" style={{ fontSize: "0.85em" }}>
                          Undone {new Date(m.undoneAt).toLocaleString()}
                          {m.undoneByName ? ` · ${m.undoneByName}` : ""}
                        </div>
                      ) : null}
                    </td>
                    <td>{m.mergedByName ?? "—"}</td>
                    <td>
                      {m.canUndo ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void undoMerge(m.id)}
                        >
                          Undo
                        </button>
                      ) : (
                        <span className="admin-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
